import { connect } from "react-redux";
import { SimpleQuestionnaire, useProgress } from "@smartintegrity/questionnaire";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import Accordion from "react-bootstrap/Accordion";
import { withNamespaces } from "react-i18next";
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Row, Spinner } from "reactstrap";
import { API_BASE_URL } from "src/modules/data-protection/constants";
import { toast } from "react-toastify";
import { mapAnswersToSave } from "src/modules/data-protection/helpers/questionnaire";
import { RiRobotLine } from "react-icons/ri";

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-2">
                <Col lg="12">
                    <p style={{width: '100%', height: '55px'}} className={`dt-field dt-skeleton`}></p>
                </Col>
            </Row>
        </div>
    );
}

const CompleteWithAIConfirmationModal = ({
    t,
    isOpen,
    toggle,
    onConfirm,
    isLoading = false
}) => {
    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            centered
            size="md"
        >
            <ModalHeader toggle={toggle}>
                {t('Complete DPIA with AI')}
            </ModalHeader>

            <ModalBody>
                <div className="text-center">
                    <div
                        className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                        style={{
                            width: 72,
                            height: 72,
                            background: "rgba(13, 110, 253, 0.1)"
                        }}
                    >
                        <RiRobotLine
                            size={36}
                            className="text-primary"
                        />
                    </div>

                    <h5 className="mb-3">
                        {t('Generate AI Suggestions')}
                    </h5>

                    <p className="text-muted mb-0">
                        {t('Would you like to complete the Data Protection Impact Assessment (DPIA) using AI suggestions?')}
                    </p>

                    <small className="text-muted d-block mt-3">
                        {t('AI will analyze the available information and generate responses for the DPIA questionnaire. The generated responses will be applied automatically and can be reviewed or updated later.')}
                    </small>
                </div>
            </ModalBody>

            <ModalFooter>
                <Button
                    color="light"
                    onClick={toggle}
                    disabled={isLoading}
                >
                    {t('Cancel')}
                </Button>

                <Button
                    color="primary"
                    onClick={onConfirm}
                    disabled={isLoading}
                >
                    {isLoading
                        ? t("Generating...")
                        : t("Generate with AI")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const ImpactAssessmentQuestionnaire = ({
    questionsQuery,
    softwareId,
    t,
    token,
    showImpactAssessment,
    onValidSubmit
}) => {
    const [isCreateAIOpen, setIsCreateAIOpen] = useState(false);
    const [ questionnaireCompleted, setQuestionnaireCompleted ] = useState(false);

    const {
        control: controlQuestionnaireProgress,
        register: registerQuestionnaireForProgress
    } = useProgress();

    const updateQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            const response = await axios.post(
                `${API_BASE_URL}/dpia/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            )

            return response.data;
        },
        onSuccess({
            data
        }) {
            toast(t("Report Submitted"), {
                type: 'success'
            });

            onValidSubmit && onValidSubmit(data);
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const completeWithAIMutation = useMutation({
        mutationFn: async () => {
            const response = await axios.post(
                `${API_BASE_URL}/dpia/impact_assessment/ai_generate`,
                {
                    softwareId: Number(softwareId)
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            )

            return response.data;
        },
        onSuccess: () => {
            questionsQuery.refetch()
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t("An error occurred while generating by AI."), {
                type: "error"
            });
        },
        onSettled: () => {
            setIsCreateAIOpen(false);
        }
    });

    const questionnaireStatusChangeCallback = useCallback((_currentPayload, newStatus) => {
        setQuestionnaireCompleted(newStatus)
    }, []);

    const handleSubmitQuestionnaire = useCallback((payload) => {
        updateQuestionnaireMutation.mutate({
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                payload
            ),
            software: softwareId,
            questionnaireType: 'data_protection_impact_assessment',
            hardware: null
        })
    }, [
        questionsQuery.data?.rawQuestions,
        updateQuestionnaireMutation,
        softwareId
    ]);

    useEffect(() => {
        registerQuestionnaireForProgress('impact-assessment-questionnaire');
    }, [ ]);

    const settings = useMemo(() => {
        let submitButtonSettings = {
            enable: showImpactAssessment && !updateQuestionnaireMutation.isLoading,
            title: updateQuestionnaireMutation.isLoading ? (
                <>
                    <Spinner 
                        size='sm' 
                        color="white"
                        className="me-1"
                    />
                    {`${t('Saving')}...`}
                </>
            ) : t('Save Changes')
        }

        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t,
        showImpactAssessment,
        updateQuestionnaireMutation.isLoading
    ]);

    if(questionsQuery.isFetching) {
        return (
            <QuestionsLoadingPlaceholder />
        )
    }

    const questionnaireTabsProgressState = controlQuestionnaireProgress.progressQuestionnaires.find(q => q.id === 'impact-assessment-questionnaire');

    const requiredQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.required || 0);
    }, 0) || 0;
    const requiredAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.requiredAnswered || 0);
    }, 0) || 0;

    const optionalQuestions = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optional || 0);
    }, 0) || 0;
    const optionalAnsweredQuestionsCount = questionnaireTabsProgressState?.tabs.reduce((acc, tab) => {
        return acc + (tab.optionalAnswered || 0);
    }, 0) || 0;


    let tabRequiredPercent = 0
    let tabOptionalPercent = 0

    if (requiredQuestions) {
        tabRequiredPercent = (requiredAnsweredQuestionsCount / requiredQuestions * 100).toFixed(0)
    }

    if (optionalQuestions) {
        tabOptionalPercent = (optionalAnsweredQuestionsCount / optionalQuestions * 100).toFixed(0)
    }

    return (
        <Accordion.Item
            className="border-none mb-2"
            eventKey="impact-assessment-questionnaire"
            disabled={!showImpactAssessment}
            style={{
                opacity: !showImpactAssessment ? "0.5" : "1",
                cursor: !showImpactAssessment ? "not-allowed" : "pointer"
            }}
        >
            <div className="d-flex justify-content-between align-items-center p-3 bg-white">
                <Accordion.Button 
                    className="bg-white"
                >
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div title={t("Available for high risk assets")}>
                            {t("Data Protection Impact Assessment")}
                        </div>

                        <div className="completed-percent">
                            <div className={`required-questions ${!requiredQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Mandatory')}: ${requiredAnsweredQuestionsCount}/${requiredQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabRequiredPercent >= 100 ? t('Completed') : `${tabRequiredPercent}%`}
                                    </span>
                                </div>
                                <Progress 
                                    className={tabRequiredPercent >= 100 ? 'completed' : 'incompleted'} 
                                    color="success" 
                                    value={tabRequiredPercent} 
                                />
                            </div>

                            <div className={`optional-questions ${!optionalQuestions ? 'd-none' : ''}`}>
                                <div className='titles'>
                                    <span className='group-title'>
                                        {`${t('Optional')}: ${optionalAnsweredQuestionsCount}/${optionalQuestions}`}
                                    </span>
                                    <span className='percent-title'>
                                        {tabOptionalPercent >= 100 ? t('Completed') : `${tabOptionalPercent}%`}
                                    </span>
                                </div>
                                <Progress 
                                    className={tabOptionalPercent >= 100 ? 'completed' : 'incompleted'} 
                                    color="success" 
                                    value={tabOptionalPercent} 
                                />
                            </div>
                        </div>
                    </div>
                </Accordion.Button>

                <div className="d-flex justify-conent-center align-items-center">
                    <CompleteWithAIConfirmationModal
                        isOpen={isCreateAIOpen}
                        toggle={() => setIsCreateAIOpen(false)}
                        onConfirm={completeWithAIMutation.mutate}
                        isLoading={completeWithAIMutation.isLoading}
                        t={t}
                    />

                    <Button
                        color="primary"
                        outline
                        size="sm"
                        style={{
                            minWidth: '160px'
                        }}
                        onClick={() => setIsCreateAIOpen(true)}
                    >
                        {t("Complete DPIA with AI")}
                    </Button>
                </div>
            </div>


            <Accordion.Body>
                <SimpleQuestionnaire 
                    questionnaireUniqueId='impact-assessment-questionnaire'
                    className='gdpr'
                    questions={questionsQuery.data?.mappedQuestions || []}
                    defaultValues={questionsQuery.data?.mappedAnswers || []}
                    settings={settings}
                    onQuestionnaireCompleteStatusChanged={questionnaireStatusChangeCallback}
                    onSubmit={handleSubmitQuestionnaire}
                    {...controlQuestionnaireProgress}
                />
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    connect((state) => {
        const { token, user } = state.Login;

        return {
            token,
            user
        };
    })(
        ImpactAssessmentQuestionnaire
    )
);